/* Vibe UI niceties — mockup parity. Namespace-clean per vibe-ownership-boundary. */

/* Language switcher: replace the (unstyleable) native select popup with a
   mockup-styled dropdown panel. Progressive enhancement: the native select
   stays in the DOM and drives the actual locale change. */
function vibeLangDropdown() {
  document.querySelectorAll('.dropdown-localization .language-selector').forEach((wrap) => {
    if (wrap.dataset.vibeEnhanced) return;
    const select = wrap.querySelector('select');
    if (!select || select.options.length < 2) return;
    wrap.dataset.vibeEnhanced = '1';

    const names = { en: 'English', fi: 'Suomi', de: 'Deutsch', sv: 'Svenska' };
    const code = (v) => (v || '').split('-')[0].toUpperCase();

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'vibe-lang__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = `<span>${code(select.value)}</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" width="10" height="10"><path d="m6 9 6 6 6-6"/></svg>`;

    const panel = document.createElement('div');
    panel.className = 'vibe-lang__panel';
    panel.setAttribute('role', 'listbox');
    panel.innerHTML = '<div class="vibe-lang__heading">Language</div>' + [...select.options].map((o) => {
      const c = code(o.value);
      const full = o.dataset.vibeFullName || names[o.value.split('-')[0]] || o.textContent.trim();
      const active = o.value === select.value ? ' is-active' : '';
      return `<button type="button" role="option" class="vibe-lang__option${active}" data-value="${o.value}"><span>${full}</span><small>${c}</small></button>`;
    }).join('');

    wrap.classList.add('vibe-lang');
    wrap.appendChild(btn);
    wrap.appendChild(panel);

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = wrap.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', String(open));
    });
    panel.addEventListener('click', (e) => {
      const opt = e.target.closest('.vibe-lang__option');
      if (!opt) return;
      select.value = opt.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      const form = select.closest('form');
      if (form && typeof form.requestSubmit === 'function') form.requestSubmit();
    });
    document.addEventListener('click', () => {
      wrap.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
    });
  });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', vibeLangDropdown);
} else {
  vibeLangDropdown();
}

/* Header dropdown: click-to-open (Timo 22.7. — hover was unreliable on his
   machine and clicking the parent jumped straight to the collection).
   A top-level menu item WITH a submenu becomes a toggle: click opens the
   dropdown instead of navigating; the "All …" first item inside handles
   the collection link. Hover still works as a bonus via Horizon's own JS. */
function vibeMenuClickToOpen() {
  // Horizon's native mega-menu is hover-driven and reveals its items
  // progressively (first open showed only the first link). Rather than fight
  // it, we hide the native submenu (CSS) and render our OWN dropdown panel from
  // the same, always-present submenu links — click-to-open, all items at once,
  // no hover, no lazy reveal. Delegated + lazily built so it survives Horizon's
  // header re-hydration. (Juuso 22.7.)
  const PARENT = '.menu-list__link[aria-haspopup="true"]';
  const isDesktop = () => !window.matchMedia('(max-width: 749px)').matches;

  const escapeHtml = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const buildPanel = (li) => {
    const native = li.querySelector('.menu-list__submenu');
    if (!native) return null;
    const links = [...native.querySelectorAll('a')]
      .map((a) => ({ t: a.textContent.trim(), href: a.getAttribute('href') }))
      .filter((x) => x.t && x.href);
    if (!links.length) return null;
    const panel = document.createElement('div');
    panel.className = 'vibe-nav-panel';
    panel.innerHTML = links
      .map((l) => `<a class="vibe-nav-panel__link" href="${escapeHtml(l.href)}">${escapeHtml(l.t)}</a>`)
      .join('');
    li.appendChild(panel);
    return panel;
  };

  const closeAll = (except) => {
    document.querySelectorAll('.menu-list__list-item.vibe-nav-open').forEach((li) => {
      if (li !== except) li.classList.remove('vibe-nav-open');
    });
  };

  document.addEventListener('click', (e) => {
    const link = e.target.closest(PARENT);
    if (link) {
      if (!isDesktop()) return;
      e.preventDefault();
      e.stopPropagation();
      const li = link.closest('.menu-list__list-item');
      if (!li.querySelector('.vibe-nav-panel')) buildPanel(li);
      const willOpen = !li.classList.contains('vibe-nav-open');
      closeAll(willOpen ? li : null);
      li.classList.toggle('vibe-nav-open', willOpen);
      return;
    }
    if (!e.target.closest('.vibe-nav-panel')) closeAll(null);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll(null);
  });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', vibeMenuClickToOpen);
} else {
  vibeMenuClickToOpen();
}

/* Lightbox: click any [data-vibe-lightbox] to open its image full-size
   (Timo 22.7. — lifestyle gallery pictures should open big on click). */
function vibeLightbox() {
  let overlay;
  const build = () => {
    overlay = document.createElement('div');
    overlay.className = 'vibe-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = '<button class="vibe-lightbox__close" aria-label="Close">&times;</button><img class="vibe-lightbox__img" alt="">';
    document.body.appendChild(overlay);
    const close = () => { overlay.classList.remove('is-open'); document.documentElement.style.overflow = ''; };
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target.closest('.vibe-lightbox__close')) close();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    return overlay;
  };
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-vibe-lightbox]');
    if (!trigger) return;
    e.preventDefault();
    if (!overlay) build();
    overlay.querySelector('.vibe-lightbox__img').src = trigger.getAttribute('data-vibe-lightbox');
    overlay.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', vibeLightbox);
} else {
  vibeLightbox();
}
